import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Select, { MultiValue, SingleValue } from 'react-select'
import { useIntl } from 'react-intl'
import moment from 'moment'
import { Range } from 'react-date-range'
import { Modal, Button } from 'react-bootstrap'
import { companyOptionsList, companySitesList, companyMeterList } from '../charts/core/_requests'
import { CompanyListItem, SelectItem } from '../charts/core/_models'
import { liveDataChartApi } from './core/_requests'
import { TagawattChartData, TagawattDataset } from './core/_models'
import FilterButton from '../charts/components/FilterButton'
import { DateRangeSelector } from '../../utils/Form/Widgets/DateRangeInput'
import LiveDataChart from './LiveDataChart'
import LiveDataSummaryCards from './LiveDataSummaryCards'
import {
  toggleCardVisibility,
  toggleSeriesVisibility,
  isMinuteKeyIncludedForToday,
} from './liveDataSeriesUtils'

type TagOption = { value: string; label: string }

const TAG_COLOR_MAP: Record<string, string> = {
  '0000b145': '#E91E63',
  '0000b146': '#9C27B0',
  '0000b147': '#FF9800',
  '0000b14b': '#00BCD4',
}

const TAG_NAME_COLOR_MAP: Record<string, string> = {
  'Hot Cubbord 25': '#E91E63',
  'Pizza 1-3': '#9C27B0',
  'Microwave 36': '#FF9800',
  'Kitchen Ring 30': '#00BCD4',
}

const READINGS_COLORS = ['#008FFB', '#00E396']
const EXTRA_TAG_COLORS = ['#FEB019', '#775DD0', '#546E7A', '#D4526E', '#8D5B4C', '#2E93fA']

const LiveDataPage: React.FC = () => {
  const intl = useIntl()

  const [companyOptions, setCompanyOptions] = useState<CompanyListItem[]>([])
  const [companySiteOptions, setCompanySiteOptions] = useState<CompanyListItem[]>([])
  const [meterOptions, setMeterOptions] = useState<CompanyListItem[]>([])

  const [companyFilter, setCompanyFilter] = useState<SingleValue<SelectItem>>(null)
  const [siteFilter, setSiteFilter] = useState<MultiValue<SelectItem>>([])
  const [meterFilter, setMeterFilter] = useState<MultiValue<SelectItem>>([])

  const [tagOptions, setTagOptions] = useState<TagOption[]>([])
  const [tagFilter, setTagFilter] = useState<MultiValue<TagOption>>([])

  const [chartType, setChartType] = useState<string>('current')
  const [rangeStart, setRangeStart] = useState<moment.Moment>(moment().subtract(7, 'days'))
  const [rangeEnd, setRangeEnd] = useState<moment.Moment>(moment())
  const [selectedRange, setSelectedRange] = useState<Range>({
    startDate: moment().subtract(7, 'days').toDate(),
    endDate: moment().toDate(),
    key: 'selection',
  })

  const [graphType, setGraphType] = useState<string>('line')
  const availableGraphTypes = [
    { value: 'line', label: 'Line' },
    { value: 'area', label: 'Area' },
    { value: 'bar', label: 'Bar' },
    { value: 'radar', label: 'Radar' },
  ]

  const [resolution, setResolution] = useState<'30min' | '1min'>('30min')
  const [liveMode, setLiveMode] = useState(false)
  const liveIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const [chartData, setChartData] = useState<TagawattChartData | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [hiddenSeriesKeys, setHiddenSeriesKeys] = useState<Set<string>>(new Set())
  const [isChartFullscreen, setIsChartFullscreen] = useState(false)
  const [showFilters, setShowFilters] = useState(false)
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < 768 : false
  )

  const DEFAULT_COMPANY_ID = '3'
  const DEFAULT_SITE_ID = '3'
  const [initialized, setInitialized] = useState(false)

  const isSingleDayCustom = useMemo(
    () => chartType === 'custom' && rangeStart.isSame(rangeEnd, 'day'),
    [chartType, rangeStart, rangeEnd]
  )

  const isIntradayView = chartType === 'current' || isSingleDayCustom

  const viewDate = useMemo(() => {
    if (chartType === 'current') return moment().format('YYYY-MM-DD')
    if (isSingleDayCustom) return rangeStart.format('YYYY-MM-DD')
    return moment().format('YYYY-MM-DD')
  }, [chartType, isSingleDayCustom, rangeStart])

  const isTodayView = useMemo(() => {
    if (chartType === 'current') return true
    if (isSingleDayCustom) return rangeStart.isSame(moment(), 'day')
    return false
  }, [chartType, isSingleDayCustom, rangeStart])

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  useEffect(() => {
    companyOptionsList().then((options) => {
      setCompanyOptions(options)
      if (!initialized) {
        const defaultCompany = options.find((o) => String(o.value) === DEFAULT_COMPANY_ID)
        if (defaultCompany) setCompanyFilter(defaultCompany)
      }
    })
  }, [])

  useEffect(() => {
    if (companyFilter) {
      companySitesList({ companyId: Number(companyFilter.value) }).then((options) => {
        setCompanySiteOptions(options)
        if (!initialized) {
          const defaultSite = options.find((o) => String(o.value) === DEFAULT_SITE_ID)
          if (defaultSite) setSiteFilter([defaultSite])
        } else {
          setSiteFilter([])
          setMeterFilter([])
        }
      })
    } else {
      setCompanySiteOptions([])
      setSiteFilter([])
      setMeterFilter([])
    }
  }, [companyFilter])

  useEffect(() => {
    if (siteFilter && siteFilter.length > 0) {
      companyMeterList({ siteId: siteFilter, meterType: 'electricity' }).then((options) => {
        setMeterOptions(options)
        if (!initialized) {
          setMeterFilter(options)
          setInitialized(true)
        } else {
          setMeterFilter([])
        }
      })
    } else {
      setMeterOptions([])
      setMeterFilter([])
    }
  }, [siteFilter])

  const buildApiParams = useCallback(() => {
    const sites = siteFilter.map((s) => s.value).join(',')
    const meters = meterFilter.map((m) => m.value).join(',')
    return {
      companyId: Number(companyFilter!.value),
      companySites: sites,
      meterType: 'Electricity',
      meterNumber: meters || undefined,
      chartType,
      customStartDate: chartType === 'custom' ? rangeStart.format('YYYY-MM-DD') : undefined,
      customEndDate: chartType === 'custom' ? rangeEnd.format('YYYY-MM-DD') : undefined,
      resolution: isIntradayView ? resolution : '30min',
    }
  }, [
    companyFilter,
    siteFilter,
    meterFilter,
    chartType,
    rangeStart,
    rangeEnd,
    resolution,
    isIntradayView,
  ])

  const extractTagOptions = (data: TagawattChartData) =>
    (data.datasets || [])
      .filter((ds: TagawattDataset) => ds.source === 'tagawatt' && ds.sensor_code)
      .map((ds: TagawattDataset) => ({
        value: ds.sensor_code!,
        label: ds.name,
      }))

  const handleRangeSelection = (range: Range) => {
    if (range.startDate && range.endDate) {
      setSelectedRange({
        startDate: range.startDate,
        endDate: range.endDate,
        key: 'selection',
      })
      setRangeStart(moment(range.startDate))
      setRangeEnd(moment(range.endDate))
    }
  }

  const handleButtonClick = (id: string) => {
    setChartType(id)
    if (id !== 'current') {
      setResolution('30min')
      setLiveMode(false)
    }
    if (id === 'custom') {
      setRangeStart(moment(selectedRange.startDate))
      setRangeEnd(moment(selectedRange.endDate))
    }
  }

  const fetchChartData = useCallback(async () => {
    if (!companyFilter) return
    try {
      setIsLoading(true)
      const data = await liveDataChartApi(buildApiParams())
      setChartData(data)
      setTagOptions(extractTagOptions(data))
    } catch (error) {
      console.error('Error fetching tagawatt chart data:', error)
    } finally {
      setIsLoading(false)
    }
  }, [companyFilter, buildApiParams])

  const silentFetchChartData = useCallback(async () => {
    if (!companyFilter || chartType !== 'current') return
    try {
      const data = await liveDataChartApi(buildApiParams())
      setChartData((prev) => {
        if (!prev) return data
        return { labels: data.labels, datasets: data.datasets }
      })
      setTagOptions(extractTagOptions(data))
    } catch (error) {
      console.error('Live fetch error:', error)
    }
  }, [companyFilter, chartType, buildApiParams])

  useEffect(() => {
    if (!companyFilter) return
    fetchChartData()
  }, [
    companyFilter,
    chartType,
    siteFilter,
    meterFilter,
    rangeStart,
    rangeEnd,
    resolution,
    fetchChartData,
  ])

  useEffect(() => {
    if (liveIntervalRef.current) {
      clearInterval(liveIntervalRef.current)
      liveIntervalRef.current = null
    }
    if (liveMode && chartType === 'current') {
      liveIntervalRef.current = setInterval(silentFetchChartData, 60000)
    }
    return () => {
      if (liveIntervalRef.current) clearInterval(liveIntervalRef.current)
    }
  }, [liveMode, chartType, silentFetchChartData])

  useEffect(() => {
    if (resolution === '1min') setLiveMode(false)
  }, [resolution])

  useEffect(() => {
    setHiddenSeriesKeys(new Set())
  }, [chartType, resolution, rangeStart, rangeEnd, companyFilter, siteFilter, meterFilter])

  useEffect(() => {
    if (!isChartFullscreen) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [isChartFullscreen])

  const handleToggleSeries = useCallback((seriesKey: string) => {
    setHiddenSeriesKeys((prev) => toggleSeriesVisibility(seriesKey, prev))
  }, [])

  const filteredDatasets = useMemo(() => {
    if (!chartData?.datasets) return []

    let datasets = chartData.datasets

    if (isIntradayView && resolution === '1min') {
      datasets = datasets.filter((ds) => ds.source === 'tagawatt' || ds.minute_data)
    }

    if (!tagFilter || tagFilter.length === 0) return datasets

    const selectedCodes = new Set(tagFilter.map((t) => t.value))
    return datasets.filter((ds) => {
      if (ds.source !== 'tagawatt') return true
      return ds.sensor_code && selectedCodes.has(ds.sensor_code)
    })
  }, [chartData, tagFilter, isIntradayView, resolution])

  const resolvedLabels = useMemo(() => {
    if (isIntradayView && resolution === '1min' && filteredDatasets.length > 0) {
      const dsWithMinute = filteredDatasets.find((ds) => ds.minute_data)
      if (dsWithMinute?.minute_data) {
        let keys = Object.keys(dsWithMinute.minute_data).sort()
        if (isTodayView) {
          const nowKey = moment().format('HH:mm')
          keys = keys.filter((k) => isMinuteKeyIncludedForToday(k, nowKey))
        }
        return keys
      }
    }
    return chartData?.labels || []
  }, [isIntradayView, resolution, filteredDatasets, chartData, isTodayView])

  const resolvedDatasets = useMemo(() => {
    if (isIntradayView && resolution === '1min') {
      return filteredDatasets.filter((ds) => ds.minute_data)
    }
    return filteredDatasets
  }, [isIntradayView, resolution, filteredDatasets])

  const handleToggleSummaryCard = useCallback((cardId: string) => {
    setHiddenSeriesKeys((prev) => toggleCardVisibility(cardId, prev))
  }, [])

  const seriesColors = useMemo(() => {
    const readingsCounter = { current: 0 }
    const tagCounter = { current: 0 }
    return resolvedDatasets.map((ds) => {
      if (ds.sensor_code && TAG_COLOR_MAP[ds.sensor_code]) return TAG_COLOR_MAP[ds.sensor_code]
      if (ds.name && TAG_NAME_COLOR_MAP[ds.name]) return TAG_NAME_COLOR_MAP[ds.name]
      if (ds.sensor_code || ds.source === 'tagawatt') {
        const idx = tagCounter.current++ % EXTRA_TAG_COLORS.length
        return EXTRA_TAG_COLORS[idx]
      }
      const idx = readingsCounter.current++
      return READINGS_COLORS[idx % READINGS_COLORS.length]
    })
  }, [resolvedDatasets])

  const colorByName = useMemo(() => ({ ...TAG_NAME_COLOR_MAP, ...TAG_COLOR_MAP }), [])

  const hasChartData = useMemo(() => {
    if (resolvedDatasets.length === 0) return false
    if (isIntradayView && resolution === '1min') {
      return resolvedDatasets.some((ds) => ds.minute_data && Object.keys(ds.minute_data).length > 0)
    }
    if (isIntradayView) {
      return resolvedDatasets.some((ds) => Array.isArray(ds.data) && ds.data.length > 0)
    }
    return (
      resolvedLabels.length > 0 &&
      resolvedDatasets.some((ds) => Array.isArray(ds.data) && ds.data.length > 0)
    )
  }, [resolvedDatasets, resolvedLabels, isIntradayView, resolution])

  const filterFields = (
    <>
      <div className='col-12 col-md-6 col-lg-3 mb-3 mb-lg-0'>
        <label className='fw-bold mb-1'>{intl.formatMessage({ id: 'FILTERFORM.COMPANY' })}:</label>
        <Select
          value={companyFilter}
          onChange={setCompanyFilter}
          options={companyOptions}
          placeholder={intl.formatMessage({ id: 'FILTERFORM.COMPANY' })}
        />
      </div>
      {companyFilter && (
        <div className='col-12 col-md-6 col-lg-3 mb-3 mb-lg-0'>
          <label className='fw-bold mb-1'>
            {intl.formatMessage({ id: 'FILTERFORM.SITESELECT' })}:
          </label>
          <Select
            value={siteFilter}
            onChange={(val: MultiValue<SelectItem>) => setSiteFilter(val)}
            options={companySiteOptions}
            isMulti
            placeholder={intl.formatMessage({ id: 'SELECTSITES' })}
          />
        </div>
      )}
      {companyFilter && siteFilter && siteFilter.length > 0 && (
        <div className='col-12 col-md-6 col-lg-3 mb-3 mb-lg-0'>
          <label className='fw-bold mb-1'>
            {intl.formatMessage({ id: 'FILTERFORM.ELECTRICMETERSSELECT' })}:
          </label>
          <Select
            value={meterFilter}
            onChange={(val: MultiValue<SelectItem>) => setMeterFilter(val)}
            options={meterOptions}
            isMulti
            placeholder={intl.formatMessage({ id: 'ALLMETERS' })}
          />
        </div>
      )}
      {companyFilter && tagOptions.length > 0 && (
        <div className='col-12 col-md-6 col-lg-3 mb-3 mb-lg-0'>
          <label className='fw-bold mb-1'>
            {intl.formatMessage({ id: 'TAGAWATT.TAGSELECT' })}:
          </label>
          <Select
            value={tagFilter}
            onChange={(val: MultiValue<TagOption>) => setTagFilter(val)}
            options={tagOptions}
            isMulti
            placeholder={intl.formatMessage({ id: 'TAGAWATT.ALLTAGS' })}
          />
        </div>
      )}
    </>
  )

  return (
    <div>
      <style>{`
        @keyframes tagawatt-live-blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.2; }
        }
      `}</style>

      {/* Filters — desktop inline */}
      {!isMobile && (
        <div className='card mb-5'>
          <div className='card-body'>
            <div className='row'>{filterFields}</div>
          </div>
        </div>
      )}

      {/* Filters — mobile modal trigger */}
      {isMobile && (
        <div className='d-flex justify-content-end mb-4'>
          <button className='btn btn-light-primary' onClick={() => setShowFilters(true)}>
            <i className='fa fa-filter me-2'></i>
            {intl.formatMessage({ id: 'FILTERS.OPTIONS' })}
          </button>
        </div>
      )}

      <Modal show={showFilters && isMobile} onHide={() => setShowFilters(false)} size='lg'>
        <Modal.Header closeButton>
          <Modal.Title>{intl.formatMessage({ id: 'FILTERS.OPTIONS' })}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className='row'>{filterFields}</div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='primary' onClick={() => setShowFilters(false)}>
            {intl.formatMessage({ id: 'LORA.APPLY', defaultMessage: 'Apply' })}
          </Button>
        </Modal.Footer>
      </Modal>

      <div className='card'>
        <div className='card-header border-0 pt-5 flex-column flex-lg-row align-items-start align-items-lg-center gap-3'>
          <h3 className='card-title align-items-start flex-column mb-0'>
            <span className='card-label fw-bold fs-3'>
              {intl.formatMessage({ id: 'TAGAWATT.DATAMONITORING' })}
            </span>
          </h3>

          <div className='card-toolbar flex-wrap gap-1' data-kt-buttons='true'>
            <FilterButton
              id='current'
              value='Current'
              isActive={chartType === 'current'}
              onClick={handleButtonClick}
            />
            <FilterButton
              id='yesterday'
              value='Yesterday'
              isActive={chartType === 'yesterday'}
              onClick={handleButtonClick}
            />
            <FilterButton
              id='last_one_week'
              value='Last 7 Days'
              isActive={chartType === 'last_one_week'}
              onClick={handleButtonClick}
            />
            <FilterButton
              id='last_one_month'
              value='Last 30 Days'
              isActive={chartType === 'last_one_month'}
              onClick={handleButtonClick}
            />
            <FilterButton
              id='last_one_year'
              value='Last 365 Days'
              isActive={chartType === 'last_one_year'}
              onClick={handleButtonClick}
            />
            <FilterButton
              id='custom'
              value='Custom'
              isActive={chartType === 'custom'}
              onClick={handleButtonClick}
            />
          </div>
        </div>

        <div className='card-body'>
          {chartType === 'custom' && (
            <div className='w-100 w-md-50 mb-5'>
              <DateRangeSelector
                name='custom_range'
                title={intl.formatMessage({ id: 'DATERANGE' })}
                seperator='to'
                ranges={[
                  {
                    startDate: rangeStart.toDate(),
                    endDate: rangeEnd.toDate(),
                    key: 'selection',
                  },
                ]}
                rangeSelected={handleRangeSelection}
              />
            </div>
          )}

          <div className='d-flex flex-wrap align-items-center gap-2 gap-md-3 mb-4'>
            <select
              className='form-select form-select-sm w-auto'
              style={{ minWidth: 100 }}
              value={graphType}
              onChange={(e) => setGraphType(e.target.value)}
            >
              {availableGraphTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {intl.formatMessage({ id: type.value })}
                </option>
              ))}
            </select>

            {isIntradayView && (
              <div className='btn-group btn-group-sm' role='group'>
                <button
                  type='button'
                  className={`btn btn-sm px-3 px-md-4 ${
                    resolution === '1min'
                      ? 'btn-active btn-active-primary active'
                      : 'btn btn-light-primary'
                  }`}
                  onClick={() => setResolution('1min')}
                >
                  1 min
                </button>
                <button
                  type='button'
                  className={`btn btn-sm px-3 px-md-4 ${
                    resolution === '30min'
                      ? 'btn-active btn-active-primary active'
                      : 'btn btn-light-primary'
                  }`}
                  onClick={() => setResolution('30min')}
                >
                  30 min
                </button>
              </div>
            )}

            {chartType === 'current' && (
              <div
                className='d-flex align-items-center gap-2'
                style={{ cursor: 'pointer', userSelect: 'none' }}
                onClick={() => setLiveMode(!liveMode)}
              >
                <div
                  style={{
                    position: 'relative',
                    width: 44,
                    height: 24,
                    borderRadius: 12,
                    backgroundColor: liveMode ? '#f44336' : '#cdd1d8',
                    transition: 'background-color 0.25s ease',
                  }}
                >
                  <div
                    style={{
                      position: 'absolute',
                      top: 3,
                      left: liveMode ? 23 : 3,
                      width: 18,
                      height: 18,
                      borderRadius: '50%',
                      backgroundColor: '#fff',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                      transition: 'left 0.25s ease',
                    }}
                  />
                </div>
                <div className='d-flex align-items-center gap-1'>
                  {liveMode && (
                    <span
                      style={{
                        display: 'inline-block',
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: '#f44336',
                        boxShadow: '0 0 6px 2px rgba(244,67,54,0.4)',
                        animation: 'tagawatt-live-blink 1s ease-in-out infinite',
                      }}
                    />
                  )}
                  <span className='fw-bold fs-7 text-gray-700'>
                    {liveMode ? 'LIVE' : 'Live Mode Disabled'}
                  </span>
                </div>
              </div>
            )}
          </div>

          <LiveDataSummaryCards
            datasets={resolvedDatasets}
            chartType={chartType}
            resolution={resolution}
            isIntradayView={isIntradayView}
            isTodayView={isTodayView}
            isLoading={isLoading}
            colorByName={colorByName}
            hiddenSeriesKeys={hiddenSeriesKeys}
            onToggleCard={handleToggleSummaryCard}
          />

          {isLoading ? (
            <div className='py-10 text-center'>
              <span className='indicator-progress' style={{ display: 'block' }}>
                {intl.formatMessage({ id: 'LOADING' })}...{' '}
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            </div>
          ) : hasChartData ? (
            <LiveDataChart
              graphType={graphType}
              resolution={resolution}
              chartType={chartType}
              viewDate={viewDate}
              isIntradayView={isIntradayView}
              isTodayView={isTodayView}
              labels={resolvedLabels}
              datasets={resolvedDatasets}
              seriesColors={seriesColors}
              hiddenSeriesKeys={hiddenSeriesKeys}
              onSeriesToggle={handleToggleSeries}
              isFullscreen={isChartFullscreen}
              onFullscreenChange={setIsChartFullscreen}
            />
          ) : companyFilter ? (
            <div className='py-10 text-center text-muted'>
              {intl.formatMessage({ id: 'NODATA' })}
            </div>
          ) : (
            <div className='py-10 text-center text-muted'>
              {intl.formatMessage({ id: 'TAGAWATT.SELECTCOMPANY' })}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default LiveDataPage
